'use client';

import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useEffect, useMemo, useRef, useState } from 'react';

import { getMemberPoints, getOffers, getPointsHistory } from '@libs/api-proxy-client';
import type { LoyaltyMemberPoints, Offer, PointsModel } from '@libs/common-types';
import {
    ensureAuth0AccessToken,
    getAccessToken,
    isAccessTokenAuth0,
    isLastActiveExpired,
    LOYALTY_LOCAL_STORAGE_KEY,
    logger,
    SESSION_EVALUATED_DOM_EVENT,
} from '@libs/core';

import { useDecisions } from '../hooks';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useUserSession } from '../hooks/useUserSession';
import {
    type CombinedUserClaims,
    excludeStoreEventOffers,
    filterOffersByType,
    filterOffersByTypeOptimizely,
    getCustomerUuid,
    hasAuth0LoyaltyId,
    isStoreEventOffer,
} from '../utils';

export interface LoyaltyContextProps {
    forceDataUpdate: boolean;
    featuredOffers: Maybe<Offer[]>;
    getOffers: () => Promise<void>;
    isActivating: boolean;
    isLoyaltyUnavailable: boolean;
    /** False until this session's initial loyalty fetch (or missing-id unavailable path) has settled. */
    hasCompletedInitialLoyaltyFetch: boolean;
    memberPoints?: Maybe<LoyaltyMemberPoints>;
    isLoading?: boolean;
    offers: Maybe<Offer[]>;
    pointsHistory?: Maybe<PointsModel[]>;
    setForceDataUpdate: Dispatch<SetStateAction<boolean>>;
    setIsActivating: Dispatch<SetStateAction<boolean>>;
    setIsLoyaltyUnavailable: Dispatch<SetStateAction<boolean>>;
    storeEventOffers: Maybe<Offer[]>;
}

export type StorageLoyaltySession = {
    offers: Maybe<Offer[]>;
    featuredOffers: Maybe<Offer[]>;
    memberPoints: Maybe<LoyaltyMemberPoints>;
    pointsHistory: Maybe<PointsModel[]>;
    storeEventOffers: Maybe<Offer[]>;
};

const EMPTY_LOYALTY_SESSION: StorageLoyaltySession = {
    offers: null,
    featuredOffers: null,
    memberPoints: null,
    pointsHistory: null,
    storeEventOffers: null,
};

export const initialLoyaltyState: LoyaltyContextProps = {
    featuredOffers: undefined,
    getOffers: async () => undefined,
    isActivating: false,
    isLoyaltyUnavailable: false,
    hasCompletedInitialLoyaltyFetch: false,
    memberPoints: undefined,
    offers: undefined,
    pointsHistory: undefined,
    setIsActivating: () => undefined,
    setIsLoyaltyUnavailable: () => undefined,
    forceDataUpdate: false,
    setForceDataUpdate: () => undefined,
    storeEventOffers: undefined,
};

export const LoyaltyContext = createContext<Maybe<LoyaltyContextProps>>(undefined);

function shouldPreserveLoyaltyWhileAuth0Recovers() {
    return !getAccessToken() && isAccessTokenAuth0() && !isLastActiveExpired();
}

export function LoyaltyProvider({ children }: { children: ReactNode }) {
    const { user, isAuthenticated } = useUserSession();

    const isAuthenticatedRef = useRef(isAuthenticated);
    isAuthenticatedRef.current = isAuthenticated;
    const [storageLoyaltyData, setStorageLoyaltyData] =
        useLocalStorage<StorageLoyaltySession>(LOYALTY_LOCAL_STORAGE_KEY);

    const [offers, setOffers] = useState<Maybe<Offer[]>>(storageLoyaltyData?.offers || undefined);
    const [featuredOffers, setFeaturedOffers] = useState<Maybe<Offer[]>>(
        storageLoyaltyData?.featuredOffers || undefined
    );
    const [storeEventOffers, setStoreEventOffers] = useState<Maybe<Offer[]>>(
        storageLoyaltyData?.storeEventOffers || undefined
    );
    const [memberPoints, setMemberPoints] = useState<Maybe<LoyaltyMemberPoints>>(
        storageLoyaltyData?.memberPoints || undefined
    );
    const [isLoading, setIsLoading] = useState(false);
    const [pointsHistory, setPointsHistory] = useState<Maybe<PointsModel[]>>(
        storageLoyaltyData?.pointsHistory || undefined
    );
    const [isActivating, setIsActivating] = useState(false);
    const [isLoyaltyUnavailable, setIsLoyaltyUnavailable] = useState(false);
    const [hasCompletedInitialLoyaltyFetch, setHasCompletedInitialLoyaltyFetch] = useState(false);
    /** Only refresh data on demand to avoid excessive network traffic */
    const [forceDataUpdate, setForceDataUpdate] = useState(false);

    const isLoyaltyApi = useDecisions('x_loyalty_api');
    const isDedicatedEventsEnabled = useDecisions('ff_ded_events');
    const isAuth0Enabled = useDecisions('auth0');
    const auth0User = user as Maybe<CombinedUserClaims>;
    const isAuth0LoyaltyIdMissing = isAuth0Enabled && !!user?.sid && !hasAuth0LoyaltyId(auth0User);

    /**
     * Create a stable user ID value using useMemo to prevent unnecessary re-renders.
     * This ensures the value only changes when the customer uuid changes, not when the
     * user object reference changes
     */
    const currentCustomerUuid = useMemo(() => getCustomerUuid(user), [user]);

    async function fetchAllData() {
        // Leave the flag false until a fetch actually runs; the effect retries when
        // auth or the access-token cookie becomes available.
        if (!isAuthenticatedRef.current) {
            return;
        }

        const accessToken = await ensureAuth0AccessToken();

        if (isAuth0LoyaltyIdMissing || !accessToken) {
            return;
        }

        try {
            const hasMemberPoints = await setMemberPointsData();

            if (!hasMemberPoints) {
                return;
            }

            setOffersData().catch(() => {});
            setPointsHistoryData().catch(() => {});
        } finally {
            setHasCompletedInitialLoyaltyFetch(true);
        }
    }

    function clearLoyaltyData() {
        setOffers(null);
        setFeaturedOffers(null);
        setStoreEventOffers(null);
        setMemberPoints(null);
        setPointsHistory(null);
        setStorageLoyaltyData(EMPTY_LOYALTY_SESSION);
    }

    function setLoyaltyDataUnavailable() {
        setIsLoyaltyUnavailable(true);
        clearLoyaltyData();
    }

    function isUsableMemberPointsResponse(response: LoyaltyMemberPoints) {
        return (
            !!response.currentTierLevel &&
            Number.isFinite(response.availablePoints) &&
            Number.isFinite(response.availableDollars)
        );
    }

    async function setMemberPointsData() {
        const userNeedsData = !memberPoints && getCustomerUuid(user);
        if (userNeedsData) {
            setIsLoading(true);
        }
        try {
            const response = await getMemberPoints();

            if (response instanceof Error) {
                setLoyaltyDataUnavailable();
                return;
            }

            if (response && 'parsedResponse' in response) {
                setLoyaltyDataUnavailable();
                return;
            }

            if (!response || !isUsableMemberPointsResponse(response)) {
                if (shouldPreserveLoyaltyWhileAuth0Recovers()) {
                    return;
                }
                setLoyaltyDataUnavailable();
                return;
            }

            setIsLoyaltyUnavailable(false);
            setMemberPoints(response);
            setStorageLoyaltyData(prev => ({
                offers: prev?.offers || null,
                featuredOffers: prev?.featuredOffers || null,
                storeEventOffers: prev?.storeEventOffers || null,
                pointsHistory: prev?.pointsHistory || null,
                memberPoints: response,
            }));

            return true;
        } catch {
            logger.error('[LoyaltyProvider] setMemberPointsData failed');
            setLoyaltyDataUnavailable();
        } finally {
            setIsLoading(false);
        }
    }

    async function setPointsHistoryData() {
        if (isAuth0LoyaltyIdMissing) {
            return;
        }

        try {
            const response = await getPointsHistory();

            if (response === null) {
                if (shouldPreserveLoyaltyWhileAuth0Recovers()) {
                    return;
                }
                setLoyaltyDataUnavailable();
                return;
            }

            setPointsHistory(response);
            setStorageLoyaltyData(prev => ({
                offers: prev?.offers || null,
                featuredOffers: prev?.featuredOffers || null,
                storeEventOffers: prev?.storeEventOffers || null,
                memberPoints: prev?.memberPoints || null,
                pointsHistory: response,
            }));
        } catch {
            logger.error('[LoyaltyProvider] setPointsHistoryData failed');
            setLoyaltyDataUnavailable();
        }
    }

    async function setOffersData() {
        if (isAuth0LoyaltyIdMissing) {
            return;
        }

        try {
            const response = await getOffers();

            if (response === null) {
                if (shouldPreserveLoyaltyWhileAuth0Recovers()) {
                    return;
                }
                setLoyaltyDataUnavailable();
                return;
            }

            const responseOffers = isLoyaltyApi
                ? filterOffersByTypeOptimizely({
                      offers: response?.offers,
                  })
                : filterOffersByType({
                      offers: response?.offers,
                  });
            const responseFeaturedOffers = isLoyaltyApi
                ? filterOffersByTypeOptimizely({
                      offers: response?.offers,
                  })
                : filterOffersByType({
                      offers: response?.offers,
                      type: 'Featured Offer',
                  });
            const filterOffers = isLoyaltyApi ? filterOffersByTypeOptimizely : filterOffersByType;
            const responseStoreEventOffers = isDedicatedEventsEnabled
                ? filterOffers({
                      offers: response?.offers,
                  }).filter(isStoreEventOffer)
                : [];
            const offersForContext = isDedicatedEventsEnabled
                ? excludeStoreEventOffers(responseOffers)
                : responseOffers;
            const featuredOffersForContext = isDedicatedEventsEnabled
                ? excludeStoreEventOffers(responseFeaturedOffers)
                : responseFeaturedOffers;

            setOffers(offersForContext);
            setFeaturedOffers(featuredOffersForContext);
            setStoreEventOffers(responseStoreEventOffers);
            setStorageLoyaltyData(prev => ({
                ...prev,
                offers: offersForContext || null,
                featuredOffers: featuredOffersForContext || null,
                storeEventOffers: responseStoreEventOffers || null,
                memberPoints: prev?.memberPoints || null,
                pointsHistory: prev?.pointsHistory || null,
            }));
        } catch {
            logger.error('[LoyaltyProvider] setOffersData failed');
            setLoyaltyDataUnavailable();
        }
    }

    useEffect(() => {
        if (forceDataUpdate) {
            setPointsHistoryData();
            setForceDataUpdate(false);
        }
    }, [forceDataUpdate]);

    const LOYALTY_FETCH_DEFER_MS = 200;

    useEffect(() => {
        if (!currentCustomerUuid) {
            return;
        }

        if (isAuth0LoyaltyIdMissing) {
            setLoyaltyDataUnavailable();
            setHasCompletedInitialLoyaltyFetch(true);
            return;
        }

        setHasCompletedInitialLoyaltyFetch(false);

        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        const scheduleFetch = () => {
            if (!isAuthenticatedRef.current) {
                return false;
            }

            if (!getAccessToken()) {
                if (isAccessTokenAuth0() && !isLastActiveExpired()) {
                    void ensureAuth0AccessToken();
                }
                return false;
            }

            timeoutId = setTimeout(() => {
                fetchAllData().catch(() => {});
            }, LOYALTY_FETCH_DEFER_MS);

            return true;
        };

        if (scheduleFetch()) {
            return () => clearTimeout(timeoutId);
        }

        const retryWhenReady = () => {
            if (scheduleFetch()) {
                globalThis.window.removeEventListener(SESSION_EVALUATED_DOM_EVENT, retryWhenReady);
            }
        };

        globalThis.window.addEventListener(SESSION_EVALUATED_DOM_EVENT, retryWhenReady);

        return () => {
            clearTimeout(timeoutId);
            globalThis.window.removeEventListener(SESSION_EVALUATED_DOM_EVENT, retryWhenReady);
        };
    }, [currentCustomerUuid, isAuth0LoyaltyIdMissing, isAuthenticated]);

    const isLoyaltyDataUnavailable = isLoyaltyUnavailable || !!isAuth0LoyaltyIdMissing;

    const contextValue = useMemo(
        () => ({
            featuredOffers,
            forceDataUpdate,
            isLoyaltyUnavailable: isLoyaltyDataUnavailable,
            hasCompletedInitialLoyaltyFetch,
            isActivating,
            memberPoints,
            offers,
            getOffers: setOffersData,
            pointsHistory,
            isLoading,
            setIsActivating,
            setIsLoyaltyUnavailable,
            setForceDataUpdate,
            storeEventOffers,
        }),
        [
            featuredOffers,
            forceDataUpdate,
            isActivating,
            isLoyaltyDataUnavailable,
            hasCompletedInitialLoyaltyFetch,
            memberPoints,
            offers,
            pointsHistory,
            isLoading,
            storeEventOffers,
            setIsActivating,
            setIsLoyaltyUnavailable,
            setForceDataUpdate,
        ]
    );

    return <LoyaltyContext.Provider value={contextValue}>{children}</LoyaltyContext.Provider>;
}
